/**
 * hooks/useClientCases.ts
 *
 * Loads all client_cases for a given clientId, ordered by created_at desc.
 * Exposes a refresh function so callers can re-fetch after mutations.
 * Updated Apr 2026 — stale response guard: fetchIdRef ensures only the latest
 *   in-flight request updates state; setCases([]) on error or client change.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';
import type { Database } from '../types/database';

export type ClientCase = Database['public']['Tables']['client_cases']['Row'];

interface UseClientCasesResult {
  cases: ClientCase[];
  loading: boolean;
  refresh: () => void;
}

export function useClientCases(clientId: string | undefined): UseClientCasesResult {
  const [cases, setCases] = useState<ClientCase[]>([]);
  const [loading, setLoading] = useState(false);
  const fetchIdRef = useRef(0);

  const fetch = useCallback(async () => {
    if (!clientId) {
      ++fetchIdRef.current;
      setCases([]);
      setLoading(false);
      return;
    }
    const fetchId = ++fetchIdRef.current;
    setCases([]);
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('client_cases')
        .select('*')
        .eq('client_id', clientId)
        .order('created_at', { ascending: false });

      if (fetchId !== fetchIdRef.current) return;
      if (error) throw error;
      setCases(data || []);
    } catch (err) {
      if (fetchId !== fetchIdRef.current) return;
      console.error('[useClientCases] fetch error:', err);
      setCases([]);
    } finally {
      if (fetchId === fetchIdRef.current) setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { fetch(); }, [fetch]);

  return { cases, loading, refresh: fetch };
}
